import { type SuggestedPrompt } from '@diagon-alley/common';
import { Group, Stack, Text } from '@mantine/core';
import { ArrowUpLeft, SparkleIcon } from '@phosphor-icons/react';
import useAiAnalystContext from '@providers/AiAnalyst/useAiAnalystContext';
import { motion } from 'framer-motion';
import { memo, useCallback, useMemo, useState } from 'react';

const suggestedPrompts = [
    {
        prompt: 'Which items have stock less than 50 days?',
        example: 'Which items have stock less than 50 days?',
    },
    {
        prompt: 'Did i receive any POs from "Instamart" and "Zepto" today?',
        example:
            'Did i receive any POs from "Instamart" and "Zepto" today?',
    },
    {
        prompt: 'Which SKUs are top sellers on "Blinkit" vs "Zepto"?',
        example: 'Which SKUs are top sellers on "Blinkit" vs "Zepto"?',
    },
];

const SuggestedActions = ({}) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    const pushTextToIAinput = useAiAnalystContext(
        (context) => context.actions.pushTextToIAinput,
    );

    const handlePromptClick = useCallback(
        async (prompt: SuggestedPrompt) => {
            pushTextToIAinput(`${prompt.example}`);
        },
        [pushTextToIAinput],
    );

    const renderPrompts = useMemo(() => {
        if (suggestedPrompts) {
            return (
                <Stack>
                    <Text className="text-sm font-medium text-gray-700">
                        Not sure what to ask? Try these suggestions:
                    </Text>
                    {suggestedPrompts.map((prompt, index) => {
                        const isHovered = hoveredIndex === index;
                        const isNextHovered = hoveredIndex === index + 1;

                        return (
                            <motion.div
                                key={prompt.prompt}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 20 }}
                                transition={{
                                    delay: index * 0.1,
                                    duration: 0.3,
                                }}
                            >
                                <Group
                                    className={`border-gray-250 relative flex cursor-pointer items-start gap-3 rounded-lg border bg-transparent p-3 hover:bg-gray-100`}
                                    key={prompt.prompt}
                                    onClick={() => handlePromptClick(prompt)}
                                    onMouseEnter={() => setHoveredIndex(index)}
                                    onMouseLeave={() => setHoveredIndex(null)}
                                    style={{
                                        backgroundColor: isHovered
                                            ? 'rgba(255, 255, 255, 0.1)'
                                            : 'transparent',
                                        transition:
                                            'background-color 200ms ease-in-out',
                                    }}
                                >
                                    <Group className="flex-1 items-center gap-2">
                                        <SparkleIcon
                                            size={14}
                                            className="text-gray-700"
                                        />
                                        <Text className="text-sm text-gray-700">
                                            {prompt.prompt}
                                        </Text>
                                    </Group>

                                    <ArrowUpLeft
                                        weight="bold"
                                        className="flex-shrink-0"
                                    />
                                </Group>
                            </motion.div>
                        );
                    })}
                </Stack>
            );
        }
        return null;
    }, [suggestedPrompts, handlePromptClick, hoveredIndex]);

    return <Stack className="gap-1">{renderPrompts}</Stack>;
};

export default memo(SuggestedActions);
